import React, { useState, useEffect } from 'react';
import { MessageSquare, Archive, Search, Users, Plus, UsersRound, ArrowLeft, Menu } from 'lucide-react';
import { useMessaging } from '../contexts/MessagingContext';
import { useAdvancedPermissions } from '../hooks/useAdvancedPermissions';
import ConversationList from './ConversationList';
import ChatInterface from './ChatInterface';
import NewConversationModal from './NewConversationModal';
import CreateGroupModal from './CreateGroupModal';
import '../Messaging.css';

const MessagingPage = ({ db, currentUser }) => {
  const {
    conversations,
    activeConversation,
    unreadCounts,
    loading,
    getFilteredConversations,
    setActiveConversation
  } = useMessaging();

  console.log('[MessagingPage] 🔍 État:', {
    activeConversation: activeConversation?.id,
    conversations: conversations.length
  });

  const { hasPermission } = useAdvancedPermissions({ userId: currentUser?.id });
  const [searchTerm, setSearchTerm] = useState('');
  const [showArchived, setShowArchived] = useState(false);
  const [showNewConversation, setShowNewConversation] = useState(false);
  const [showCreateGroup, setShowCreateGroup] = useState(false);
  const [activeTab, setActiveTab] = useState('internal');
  const [isMobileView, setIsMobileView] = useState(false);
  const [showConversationList, setShowConversationList] = useState(true);

  // Détection du mode mobile avec gestion d'état robuste
  useEffect(() => {
    const checkMobile = () => {
      const mobile = window.innerWidth <= 768;
      setIsMobileView(mobile);
      
      // Sur mobile, gérer l'affichage selon l'état de la conversation
      if (mobile) {
        // Si on a une conversation active, afficher la vue chat
        // Sinon, afficher la liste des conversations
        setShowConversationList(!activeConversation);
      }
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, [activeConversation]);

  // Gérer les transitions entre liste et conversation sur mobile
  useEffect(() => {
    if (isMobileView) {
      // Si on a une conversation active, cacher la liste
      // Sinon, afficher la liste
      setShowConversationList(!activeConversation);
    }
  }, [activeConversation, isMobileView]);

  // Correction iOS - Gestion du clavier et de l'en-tête
  useEffect(() => {
    const isIOS = /iPhone|iPad|iPod/.test(navigator.userAgent);
    if (isIOS && isMobileView) {
      // Cacher l'en-tête de l'application quand une conversation est ouverte
      const appHeader = document.querySelector('.main-header, .app-header, header');
      if (appHeader && activeConversation) {
        appHeader.style.display = 'none';
      } else if (appHeader) {
        appHeader.style.display = 'flex';
      }

      // Gestion du clavier iOS
      const handleFocus = () => {
        const chatContainer = document.querySelector('.mobile-chat-container');
        if (chatContainer) {
          chatContainer.classList.add('keyboard-open');
        }
      };

      const handleBlur = () => {
        const chatContainer = document.querySelector('.mobile-chat-container');
        if (chatContainer) {
          chatContainer.classList.remove('keyboard-open');
        }
      };

      // Écouter les événements de focus/blur sur les inputs
      const messageInputs = document.querySelectorAll('.message-input');
      messageInputs.forEach(input => {
        input.addEventListener('focus', handleFocus);
        input.addEventListener('blur', handleBlur);
      });

      return () => {
        // Nettoyer les écouteurs d'événements sur les inputs
        messageInputs.forEach(input => {
          input.removeEventListener('focus', handleFocus);
          input.removeEventListener('blur', handleBlur);
        });
      };
    }
  }, [isMobileView, activeConversation]);

  // Navigation vers la liste des conversations
  const handleBackToList = () => {
    setActiveConversation(null);
    setShowConversationList(true);
  };

  // Navigation vers une conversation
  const handleSelectConversation = (conversation) => {
    if (isMobileView) {
      setShowConversationList(false);
    }
  };

  // Vérifier les permissions
  const canViewMessaging = hasPermission({ resource: 'messaging', action: 'read' });
  const canWriteMessaging = hasPermission({ resource: 'messaging', action: 'write' });

  // Filtrer les conversations
  const filteredConversations = getFilteredConversations(activeTab, showArchived).filter(conv => {
    if (!searchTerm) return true;
    
    // Recherche dans le dernier message ou les participants
    const searchLower = searchTerm.toLowerCase();
    return conv.lastMessage?.toLowerCase().includes(searchLower);
  });

  if (!canViewMessaging) {
    return (
      <div className="messaging-container">
        <div className="messaging-access-denied">
          <MessageSquare size={48} className="messaging-icon-denied" />
          <h2>Accès refusé</h2>
          <p>Vous n'avez pas les permissions nécessaires pour accéder à la messagerie.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="messaging-container">
      {/* Mode mobile - Navigation plein écran */}
      {isMobileView ? (
        <>
          {/* Vue liste des conversations */}
          {showConversationList && (
            <div className="mobile-conversation-list">
              {/* En-tête mobile */}
              <div className="mobile-header">
                <div className="mobile-header-title">
                  <MessageSquare size={24} />
                  <h1>Messagerie</h1>
                </div>
                <div className="mobile-header-actions">
                  {/* Badges de messages non lus */}
                  <div className="mobile-badges">
                    {unreadCounts.internal > 0 && (
                      <span className="badge badge-internal">
                        {unreadCounts.internal}
                      </span>
                    )}
                    {unreadCounts.client > 0 && (
                      <span className="badge badge-client">
                        {unreadCounts.client}
                      </span>
                    )}
                    {unreadCounts.group > 0 && (
                      <span className="badge badge-group">
                        {unreadCounts.group}
                      </span>
                    )}
                  </div>

                  {/* Bouton nouvelle conversation */}
                  {canWriteMessaging && (
                    <button 
                      className="mobile-new-conversation"
                      onClick={() => {
                        if (activeTab === 'group') {
                          setShowCreateGroup(true);
                        } else {
                          setShowNewConversation(true);
                        }
                      }}
                    >
                      <Plus size={20} />
                    </button>
                  )}
                </div>
              </div>

              {/* Barre de recherche mobile */}
              <div className="mobile-search-container">
                <Search size={16} className="mobile-search-icon" />
                <input
                  type="text"
                  placeholder="Rechercher une conversation..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="mobile-search-input"
                />
              </div>

              {/* Onglets mobiles */}
              <div className="mobile-conversation-tabs">
                <button
                  className={`mobile-tab ${activeTab === 'internal' ? 'active' : ''}`}
                  onClick={() => setActiveTab('internal')}
                >
                  <Users size={16} />
                  <span>Collaborateurs</span>
                  {unreadCounts.internal > 0 && (
                    <span className="mobile-tab-badge">{unreadCounts.internal}</span>
                  )}
                </button>
                <button
                  className={`mobile-tab ${activeTab === 'client' ? 'active' : ''}`}
                  onClick={() => setActiveTab('client')}
                >
                  <MessageSquare size={16} />
                  <span>Bénéficiaires</span>
                  {unreadCounts.client > 0 && (
                    <span className="mobile-tab-badge">{unreadCounts.client}</span>
                  )}
                </button>
                <button
                  className={`mobile-tab ${activeTab === 'group' ? 'active' : ''}`}
                  onClick={() => setActiveTab('group')}
                >
                  <UsersRound size={16} />
                  <span>Groupes</span>
                  {unreadCounts.group > 0 && (
                    <span className="mobile-tab-badge">{unreadCounts.group}</span>
                  )}
                </button>
              </div>

              {/* Filtre archivé mobile */}
              <div className="mobile-archive-filter">
                <button
                  className={`mobile-btn-archive ${showArchived ? 'active' : ''}`}
                  onClick={() => setShowArchived(!showArchived)}
                >
                  <Archive size={16} />
                  {showArchived ? 'Conversations actives' : 'Conversations archivées'}
                </button>
              </div>

              {/* Liste des conversations mobile */}
              <div className="mobile-conversation-list-container">
                <ConversationList
                  conversations={filteredConversations}
                  loading={loading}
                  currentUser={currentUser}
                  activeTab={activeTab}
                  db={db}
                  onSelectConversation={handleSelectConversation}
                />
              </div>
            </div>
          )}

          {/* Vue conversation active */}
          {!showConversationList && activeConversation && (
            <div className="mobile-chat-view">
              {/* En-tête de conversation mobile */}
              <div className="mobile-chat-header">
                <button 
                  className="mobile-back-button"
                  onClick={handleBackToList}
                >
                  <ArrowLeft size={20} />
                </button>
                <div className="mobile-chat-title">
                  <h2>{activeConversation.groupName || activeConversation.participantNames || 'Conversation'}</h2>
                  {activeConversation.type === 'group' && (
                    <span className="mobile-chat-subtitle">
                      Groupe • {activeConversation.participants?.length || 0} membres
                    </span>
                  )}
                </div>
                <div className="mobile-chat-actions">
                  {/* Actions de conversation (infos, etc.) */}
                </div>
              </div>

              {/* Interface de chat mobile */}
              <div className="mobile-chat-container">
                <ChatInterface
                  currentUser={currentUser}
                  canWrite={canWriteMessaging}
                  db={db}
                  isMobile={true}
                />
              </div>
            </div>
          )}
        </>
      ) : (
        /* Mode desktop - Interface classique */
        <>
          {/* En-tête */}
          <div className="messaging-header">
            <div className="messaging-title">
              <MessageSquare size={24} />
              <h1>Messagerie</h1>
            </div>
            
            {/* Badges de messages non lus */}
            <div className="messaging-badges">
              {unreadCounts.internal > 0 && (
                <span className="badge badge-internal">
                  {unreadCounts.internal}
                </span>
              )}
              {unreadCounts.client > 0 && (
                <span className="badge badge-client">
                  {unreadCounts.client}
                </span>
              )}
              {unreadCounts.group > 0 && (
                <span className="badge badge-group">
                  {unreadCounts.group}
                </span>
              )}
            </div>

            {/* Bouton nouvelle conversation */}
            {canWriteMessaging && (
              <button 
                className="btn-new-conversation"
                onClick={() => {
                  if (activeTab === 'group') {
                    setShowCreateGroup(true);
                  } else {
                    setShowNewConversation(true);
                  }
                }}
              >
                <Plus size={16} />
                {activeTab === 'group' ? 'Créer un groupe' : 'Nouvelle conversation'}
              </button>
            )}
          </div>

          {/* Contenu principal */}
          <div className="messaging-content">
            {/* Barre latérale des conversations */}
            <div className="conversations-sidebar">
              {/* Barre de recherche */}
              <div className="search-container">
                <Search size={16} className="search-icon" />
                <input
                  type="text"
                  placeholder="Rechercher une conversation..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="search-input"
                />
              </div>

              {/* Onglets */}
              <div className="conversation-tabs">
                <button
                  className={`tab ${activeTab === 'internal' ? 'active' : ''}`}
                  onClick={() => setActiveTab('internal')}
                >
                  <Users size={16} />
                  Collaborateurs
                  {unreadCounts.internal > 0 && (
                    <span className="tab-badge">{unreadCounts.internal}</span>
                  )}
                </button>
                <button
                  className={`tab ${activeTab === 'client' ? 'active' : ''}`}
                  onClick={() => setActiveTab('client')}
                >
                  <MessageSquare size={16} />
                  Bénéficiaires
                  {unreadCounts.client > 0 && (
                    <span className="tab-badge">{unreadCounts.client}</span>
                  )}
                </button>
                <button
                  className={`tab ${activeTab === 'group' ? 'active' : ''}`}
                  onClick={() => setActiveTab('group')}
                >
                  <UsersRound size={16} />
                  Groupes
                  {unreadCounts.group > 0 && (
                    <span className="tab-badge">{unreadCounts.group}</span>
                  )}
                </button>
              </div>

              {/* Filtre archivé */}
              <div className="archive-filter">
                <button
                  className={`btn-archive ${showArchived ? 'active' : ''}`}
                  onClick={() => setShowArchived(!showArchived)}
                >
                  <Archive size={16} />
                  {showArchived ? 'Conversations actives' : 'Conversations archivées'}
                </button>
              </div>

              {/* Liste des conversations */}
              <ConversationList
                conversations={filteredConversations}
                loading={loading}
                currentUser={currentUser}
                activeTab={activeTab}
                db={db}
              />
            </div>

            {/* Interface de chat */}
            <div className="chat-container">
              {activeConversation ? (
                <ChatInterface
                  currentUser={currentUser}
                  canWrite={canWriteMessaging}
                  db={db}
                />
              ) : (
                <div className="chat-placeholder">
                  <MessageSquare size={48} className="placeholder-icon" />
                  <h3>Sélectionnez une conversation</h3>
                  <p>Choisissez une conversation dans la liste pour commencer à discuter.</p>
                  {canWriteMessaging && (
                    <button 
                      className="btn-start-conversation"
                      onClick={() => setShowNewConversation(true)}
                    >
                      <Plus size={16} />
                      Démarrer une conversation
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {/* Modal nouvelle conversation */}
      {showNewConversation && (
        <NewConversationModal
          onClose={() => setShowNewConversation(false)}
          currentUser={currentUser}
          db={db}
        />
      )}

      {/* Modal création de groupe */}
      {showCreateGroup && (
        <CreateGroupModal
          onClose={() => setShowCreateGroup(false)}
          currentUser={currentUser}
          db={db}
        />
      )}

      {/* La notification push est maintenant gérée au niveau du MainLayout */}
    </div>
  );
};

export default MessagingPage;
